import { type MouseEvent, type ReactNode, useState } from 'react';

import { CheckIcon, CopyIcon } from 'lucide-react';

import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import useTMSContext from 'hooks/useTMSContext';
import { TMS } from 'types/enums/Integrations';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import { copyToClipboard } from 'utils/copyToClipboard';
import { flattenSuggestionChanges } from 'utils/flattenSuggestionChanges';
import { cn } from 'utils/shadcn';
import {
  formatFileName,
  getFieldOrder,
  reorderFields,
} from 'utils/suggestions/suggestionFormat';

import {
  isLoadBuildingLocationSection,
  loadBuildingLocationSectionHasContent,
} from '../utils/formatLoadBuildingPreview';
import { groupAndMergeFields } from '../utils/suggestionFieldGrouping';
import {
  getSuggestionPreviewConfig,
  hasPreviewableSuggestionChanges,
} from '../utils/suggestionPreviewConfig';
import { SuggestionPreviewSection } from './SuggestionPreviewSection';

type SuggestionPreviewProps = {
  suggestion: GenericSuggestion;
  ordinal: string;
  descriptor: string;
};

function getFlattenedChanges(
  suggestion: GenericSuggestion
): Record<string, unknown> {
  const changes =
    suggestion.pipeline === SuggestionPipelines.CheckCall
      ? suggestion.suggested.checkCallChanges
      : suggestion.suggested;

  return flattenSuggestionChanges(changes);
}

// Production debug label for the entity backing this Kit suggestion.
function SuggestionDebugId({ suggestion }: { suggestion: GenericSuggestion }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = await copyToClipboard(String(suggestion.id), {
      showToast: false,
    });
    if (!ok) {
      return;
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Flex align='center' gap='xs' className='pt-1 pb-0.5 min-w-0'>
      <Typography
        variant='body-xxs'
        className='text-neutral-400 italic text-[10px] leading-normal min-w-0'
      >
        Suggestion ID: {suggestion.id}
      </Typography>

      <button
        type='button'
        onClick={handleCopy}
        aria-label='Click to copy suggestion ID'
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded ml-1 p-0.5',
          'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100',
          'cursor-pointer'
        )}
      >
        {copied ? (
          <CheckIcon
            className='h-2.5 w-2.5 !stroke-success'
            strokeWidth={1.5}
          />
        ) : (
          <CopyIcon
            className='h-2.5 w-2.5 !stroke-neutral-400'
            strokeWidth={1.5}
          />
        )}
      </button>
    </Flex>
  );
}

export function SuggestionPreview({
  suggestion,
  ordinal,
  descriptor,
}: SuggestionPreviewProps) {
  const { tmsName: tmsNameFromContext } = useTMSContext();
  const tmsName = (tmsNameFromContext || TMS.Mcleod) as TMS;

  const flattenedChanges = getFlattenedChanges(suggestion);

  if (!hasPreviewableSuggestionChanges(flattenedChanges, suggestion.pipeline)) {
    return (
      <div className='px-2 pt-1.5 pb-2'>
        <Typography variant='body-xs'>No changes available</Typography>
        <SuggestionDebugId suggestion={suggestion} />
      </div>
    );
  }

  const isLoadBuilding =
    suggestion.pipeline === SuggestionPipelines.LoadBuilding;

  const { sectionOrder, sectionLabels } = getSuggestionPreviewConfig(
    suggestion.pipeline
  );

  const priorityOrder = getFieldOrder(suggestion.pipeline);
  const orderedFields = reorderFields(
    Object.entries(flattenedChanges),
    priorityOrder
  );
  const { mergedSections, ungrouped } = groupAndMergeFields(
    orderedFields,
    suggestion.pipeline,
    flattenedChanges,
    tmsName
  );

  const previewSections: { key: string; node: ReactNode }[] = [];

  for (const sectionKey of sectionOrder) {
    const sectionFields = mergedSections[sectionKey];
    const isLoadBuildingLocation =
      isLoadBuilding && isLoadBuildingLocationSection(sectionKey);

    if (isLoadBuildingLocation) {
      if (
        !loadBuildingLocationSectionHasContent(
          sectionKey,
          flattenedChanges,
          tmsName
        )
      ) {
        continue;
      }

      previewSections.push({
        key: sectionKey,
        node: (
          <SuggestionPreviewSection
            label={sectionLabels[sectionKey] || sectionKey}
            isLoadBuildingAddress
            sectionKey={sectionKey}
            flattenedChanges={flattenedChanges}
            tmsName={tmsName}
          />
        ),
      });
      continue;
    }

    if (!sectionFields || sectionFields.length === 0) {
      continue;
    }

    previewSections.push({
      key: sectionKey,
      node: (
        <SuggestionPreviewSection
          label={sectionLabels[sectionKey] || sectionKey}
          fields={sectionFields.map((field, idx) => ({
            key: `${sectionKey}-${idx}`,
            label: field.label,
            value: field.value,
          }))}
        />
      ),
    });
  }

  if (ungrouped.length > 0) {
    previewSections.push({
      key: 'ungrouped',
      node: (
        <SuggestionPreviewSection
          label={previewSections.length > 0 ? 'Other' : undefined}
          fields={ungrouped.map((field, idx) => ({
            key: `ungrouped-${idx}`,
            label: field.label,
            value: field.value,
          }))}
        />
      ),
    });
  }

  return (
    <div className='px-2 pt-1.5 pb-2 w-full max-h-[320px] overflow-y-auto'>
      <Flex direction='col' gap='none' className='gap-y-1'>
        <div className='w-full pb-1 border-b border-neutral-200'>
          <Flex align='start' gap='xs' className='min-w-0'>
            <Typography
              variant='body-xs'
              className='shrink-0 tabular-nums text-neutral-400 leading-tight'
            >
              {ordinal}
            </Typography>
            <Typography
              variant='body-xs'
              weight='semibold'
              className='min-w-0 text-neutral-800 wrap-break-word leading-tight'
            >
              {descriptor}
            </Typography>
          </Flex>
          {suggestion.attachment?.originalFileName && (
            <Typography
              variant='body-xs'
              className='text-neutral-500 italic mt-0.5 text-[11px]'
            >
              From: {formatFileName(suggestion.attachment.originalFileName)}
            </Typography>
          )}
        </div>

        <Flex direction='col' gap='none'>
          {previewSections.map(({ key, node }) => (
            <div key={key} className='py-1 first:pt-0'>
              {node}
            </div>
          ))}
        </Flex>

        <SuggestionDebugId suggestion={suggestion} />
      </Flex>
    </div>
  );
}
